import routingService from 'common/src/modules/routing/routing.service';
import getDisplayNameForUrl from 'common/src/modules/routing/utils/getDisplayNameForUrl';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {GeneralNavigationSkillToolName} from '../../generalNavigationSkill.enums';
import formatNavigationHistory from '../utils/formatNavigationHistory';
import {BrowserHistoryAction} from './browserHistoryTool.enums';

const inputSchema = z.object({
  action: z
    .nativeEnum(BrowserHistoryAction)
    .describe(
      `Action to perform: "${BrowserHistoryAction.Read}" to get navigation history, "${BrowserHistoryAction.GoBack}" to navigate to previous page, "${BrowserHistoryAction.GoForward}" to navigate forward`,
    ),
});

async function execute({
  input: {action},
}: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
  switch (action) {
    case BrowserHistoryAction.Read: {
      return toolResult(formatNavigationHistory(routingService.getNavigationHistory()));
    }

    case BrowserHistoryAction.GoBack: {
      await routingService.goBack();
      const pageLabel = _getCurrentPageLabel();

      return toolResult(`Navigated back to ${pageLabel}.`);
    }

    case BrowserHistoryAction.GoForward: {
      await routingService.goForward();
      const pageLabel = _getCurrentPageLabel();

      return toolResult(`Navigated forward to ${pageLabel}.`);
    }
  }
}

function _getCurrentPageLabel(): string {
  const url = routingService.getCurrentLocationUrl();
  const displayName = getDisplayNameForUrl(url);

  return displayName === url ? url : `${displayName} (${url})`;
}

function browserHistoryTool() {
  return tool({
    name: GeneralNavigationSkillToolName.BrowserHistory,
    displayName: 'Looking around',
    description:
      'Read navigation history to see pages the user has visited, or navigate back/forward through the history',
    execute,
    inputSchema,
  });
}

export default browserHistoryTool;
